/**
 * Add address (slice 2h §4, §8.1; Paper 71–73, 71b–73b): a dialog on a
 * desktop, a sheet on a phone.
 *
 * 1. The address: a pending link with a code, in place of one in progress.
 * 2. Email this code from exactly that address: the code, **Email it** (a
 *    `mailto:` with the code as the subject), Copy code and Copy address,
 *    the countdown. It waits on the `messaging` hub event, which refetches
 *    the page's data; past its 10 minutes, New code.
 * 3. Linked: save the crew's address as a contact named Crew.
 *
 * The step comes from the page's data for the link the window made; only
 * the address typed and the code (`heldCodes`) are its own.
 */
import { Button, buttonVariants } from "@crew/ui/components/button";
import { Input } from "@crew/ui/components/input";
import { cn } from "@crew/ui/lib/utils";
import { Mail } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { errorHint } from "../../app/api";
import {
  useCreateEmailLink,
  useHeldCode,
  useRemoveLink,
  type EmailLink,
  type EmailView,
} from "./api";
import { useServerSeconds } from "./clock";
import { addressState } from "./email-view";
import { WaitingArc } from "./parts";
import { BIG, useCopy } from "./window-kit";
import { Footer, Head, WindowShell } from "./window-parts";

/** What opens the window: a new address, or the code a row shows. */
export type EmailWindowOpen = { linkId: string | null };

export function EmailWindow({
  open,
  email,
  mobile,
  onClose,
  onWindow,
}: {
  open: EmailWindowOpen | null;
  email: EmailView;
  mobile: boolean;
  onClose: () => void;
  onWindow: (open: EmailWindowOpen) => void;
}) {
  return (
    <WindowShell open={open !== null} mobile={mobile} onClose={onClose}>
      {open ? (
        <EmailSteps
          key={open.linkId ?? "new"}
          start={open.linkId}
          email={email}
          mobile={mobile}
          onClose={onClose}
          onWindow={onWindow}
        />
      ) : null}
    </WindowShell>
  );
}

/** The window's steps, apart from its frame so a test can render them. */
export function EmailSteps({
  start,
  email,
  mobile,
  onClose,
  onWindow,
}: {
  start: string | null;
  email: EmailView;
  mobile: boolean;
  onClose: () => void;
  onWindow: (open: EmailWindowOpen) => void;
}) {
  const [linkId, setLinkId] = useState(start);
  const link = linkId ? email.links.find((l) => l.id === linkId) : undefined;
  if (!linkId || !link) {
    return (
      <AddressStep
        mobile={mobile}
        notice={linkId ? "That code isn't waiting any more. Start again with the address." : null}
        onCancel={onClose}
        onMade={setLinkId}
      />
    );
  }
  if (link.status === "linked") {
    return <LinkedStep link={link} crew={email.address!.full} mobile={mobile} onClose={onClose} />;
  }
  return (
    <CodeStep
      link={link}
      crew={email.address!.full}
      mobile={mobile}
      onClose={onClose}
      onNewCode={(id) => onWindow({ linkId: id })}
    />
  );
}

// ── 1. The address ──────────────────────────────────────────────────────

const NEXT = "Next, you'll email us a code from it.";

function AddressStep({
  mobile,
  notice,
  onCancel,
  onMade,
}: {
  mobile: boolean;
  notice: string | null;
  onCancel: () => void;
  onMade: (linkId: string) => void;
}) {
  const [address, setAddress] = useState("");
  const create = useCreateEmailLink();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!address.trim()) return;
    create.mutate(address.trim(), { onSuccess: ({ link }) => onMade(link.id) });
  };
  const error = create.error ? errorHint(create.error) : null;
  return (
    <form onSubmit={submit} data-email-step="address" className="flex flex-col">
      <Head
        mobile={mobile}
        eyebrow="Step 1 of 2"
        title="Add an address"
        line="Mail from it will reach your crew as you. Use one you send from: a work address, a personal one."
      />
      <div className={cn("flex flex-col gap-2", mobile ? "px-5 pt-4.5" : "px-7 py-6")}>
        <label htmlFor="email-address" className="text-sm leading-5 font-medium text-foreground">
          Email address
        </label>
        <Input
          id="email-address"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoFocus
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="you@example.com"
          aria-invalid={Boolean(error) || undefined}
          className={cn(
            "h-auto rounded-md bg-card focus-visible:border-[1.5px] focus-visible:border-foreground",
            mobile ? "p-3 text-base leading-5" : "px-3 py-2.5 text-body leading-5",
          )}
        />
        {error ? (
          <p role="alert" className="text-meta leading-4.5 text-destructive">
            {error}
          </p>
        ) : (
          <p className="text-meta leading-4.5 text-muted-foreground">
            {notice ?? "Only you see it. You can link up to 5."}
          </p>
        )}
      </div>
      {mobile ? (
        <div className="flex flex-col gap-2.5 px-5 pt-4.5 pb-8.5">
          <Button type="submit" size="lg" className={BIG} disabled={create.isPending}>
            Continue
          </Button>
          <p className="text-center text-meta leading-4.5 text-hint">{NEXT}</p>
        </div>
      ) : (
        <Footer line={<span className="text-meta leading-4.5 text-hint">{NEXT}</span>}>
          <Button type="button" variant="ghost" size="lg" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" size="lg" disabled={create.isPending}>
            Continue
          </Button>
        </Footer>
      )}
    </form>
  );
}

// ── 2. Email the code ───────────────────────────────────────────────────

/** The code, large, and where it goes from and to (Paper 72, 72b). */
function CodePanel({
  code,
  from,
  crew,
  mobile,
  children,
}: {
  code: string | null;
  from: string;
  crew: string;
  mobile: boolean;
  children?: ReactNode;
}) {
  const shown = code ?? "CREW-······";
  if (mobile) {
    return (
      <div className="mx-5 mt-4.5 flex flex-col gap-0.5 rounded-lg bg-background p-4">
        <span
          data-email-code={code ?? undefined}
          className="font-mono text-title leading-9 font-medium tracking-[0.04em] text-foreground"
        >
          {shown}
        </span>
        <span className="truncate text-sm leading-5 text-muted-foreground">to {crew}</span>
      </div>
    );
  }
  return (
    <div className="mx-7 mt-6 flex flex-col gap-1 rounded-lg bg-background p-5">
      <span className="text-meta leading-4.5 text-muted-foreground">Send</span>
      <span
        data-email-code={code ?? undefined}
        className="font-mono text-[34px] leading-10 font-medium tracking-[0.04em] text-foreground"
      >
        {shown}
      </span>
      <div className="flex gap-6 pt-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-meta leading-4.5 text-muted-foreground">from</span>
          <span className="text-sm leading-5 font-semibold wrap-anywhere text-foreground">
            {from}
          </span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-meta leading-4.5 text-muted-foreground">to</span>
          <span className="text-sm leading-5 font-semibold wrap-anywhere text-foreground">
            {crew}
          </span>
        </div>
      </div>
      {children ? <div className="flex flex-wrap gap-2 pt-4">{children}</div> : null}
    </div>
  );
}

/** The waiting arc and the countdown (Paper 72, 72b). */
function Waiting({ left, mobile }: { left: string; mobile: boolean }) {
  return (
    <span className={cn("flex min-w-0 items-center gap-2.5", mobile && "justify-center gap-2")}>
      <WaitingArc size={mobile ? 14 : 16} />
      {mobile ? (
        <span className="text-meta leading-4.5 text-muted-foreground">
          Waiting for your email · {left} left
        </span>
      ) : (
        <span className="text-sm leading-5 text-copy">
          Waiting for your email{" "}
          <span className="text-meta text-hint">· code works for {left}</span>
        </span>
      )}
    </span>
  );
}

/** Step 2's line, per state (Paper 72, 72b). */
function codeLine(mode: CodeMode, address: string, mobile: boolean): string {
  if (mode === "code") {
    return mobile
      ? `Send it from exactly ${address}. Tap below: your mail app opens with it written.`
      : "Send it from exactly this address, in the subject or the message.";
  }
  if (mode === "expired")
    return "That code has expired. Get a new one, and email it within 10 minutes.";
  return "This code was shown in another tab. Get a new one here.";
}

/** What step 2 shows: the code, a code this tab doesn't hold, or one past its 10 minutes. */
type CodeMode = "code" | "lost" | "expired";

/** What step 2's halves share. */
type CodeParts = {
  mode: CodeMode;
  left: string;
  creating: boolean;
  removing: boolean;
  onNewCode: () => void;
  onCancel: () => void;
};

/** Opens the mail app with the code written (Paper 72's Email it, 72b's Email the code). */
function EmailIt({ href, big }: { href: string | null; big: boolean }) {
  return (
    <a
      href={href ?? undefined}
      className={cn(
        buttonVariants({ size: big ? "lg" : "default" }),
        big ? cn(BIG, "gap-2") : "h-8 gap-1.5 px-3 text-meta",
      )}
    >
      <Mail aria-hidden className={big ? "size-4.5" : "size-3.5"} />
      {big ? "Email the code" : "Email it"}
    </a>
  );
}

/** Email it, Copy code and Copy address, in the code's panel on a desktop (Paper 72). */
function PanelButtons({
  mailto,
  code,
  crew,
}: {
  mailto: string | null;
  code: string;
  crew: string;
}) {
  const [codeCopied, copyCode] = useCopy();
  const [addressCopied, copyAddress] = useCopy();
  const outline = "h-8 bg-card px-3 text-meta";
  return (
    <>
      <EmailIt href={mailto} big={false} />
      <Button variant="outline" className={outline} onClick={() => copyCode(code)}>
        {codeCopied ? "Copied" : "Copy code"}
      </Button>
      <Button variant="outline" className={outline} onClick={() => copyAddress(crew)}>
        {addressCopied ? "Copied" : "Copy address"}
      </Button>
    </>
  );
}

/**
 * Step 2 on a phone (Paper 72b): Email the code (`mailto`, null when this tab
 * doesn't hold it) and the countdown, or New code; Cancel.
 */
function PhoneActions({ mailto, ...p }: CodeParts & { mailto: string | null }) {
  return (
    <div className="flex flex-col gap-2.5 px-5 pt-4.5 pb-8.5">
      {p.mode === "code" ? (
        <>
          <EmailIt href={mailto} big />
          <Waiting left={p.left} mobile />
        </>
      ) : (
        <Button size="lg" className={BIG} disabled={p.creating} onClick={p.onNewCode}>
          New code
        </Button>
      )}
      <Button variant="ghost" size="lg" className="h-11" disabled={p.removing} onClick={p.onCancel}>
        Cancel
      </Button>
    </div>
  );
}

/** Step 2's desktop footer (Paper 72): the countdown, or why it stopped and New code; Cancel. */
function DesktopActions(p: CodeParts) {
  const waiting = p.mode === "code";
  const stopped = p.mode === "expired" ? "Code expired." : "Code hidden.";
  return (
    <div className="pt-6">
      <Footer
        line={
          waiting ? (
            <Waiting left={p.left} mobile={false} />
          ) : (
            <span className="text-sm leading-5 text-copy">{stopped}</span>
          )
        }
      >
        <Button variant="ghost" size="lg" disabled={p.removing} onClick={p.onCancel}>
          Cancel
        </Button>
        {waiting ? null : (
          <Button size="lg" disabled={p.creating} onClick={p.onNewCode}>
            New code
          </Button>
        )}
      </Footer>
    </div>
  );
}

const CODE_TITLE = {
  phone: "Email this code to link",
  desktop: "Email this code from that address",
};

/** Why a change was refused, under the step's head. */
function ErrorLine({ error, mobile }: { error: unknown; mobile: boolean }) {
  if (!error) return null;
  return (
    <p role="alert" className={cn("pt-3 text-meta text-destructive", mobile ? "px-5" : "px-7")}>
      {errorHint(error)}
    </p>
  );
}

/** Step 2's state: the link's code (if this tab holds it), its countdown, and New code and Cancel. */
function useCodeStep(link: EmailLink, onClose: () => void, onNewCode: (linkId: string) => void) {
  const state = addressState(link, useServerSeconds(true));
  const held = useHeldCode(link.id);
  const remove = useRemoveLink();
  const create = useCreateEmailLink();
  const mode: CodeMode = state.kind !== "pending" ? "expired" : held ? "code" : "lost";
  const parts: CodeParts = {
    mode,
    left: state.kind === "pending" ? state.left : "0:00",
    creating: create.isPending,
    removing: remove.isPending,
    onNewCode: () =>
      create.mutate(link.address, { onSuccess: ({ link: made }) => onNewCode(made.id) }),
    onCancel: () => remove.mutate(link.id, { onSuccess: onClose }),
  };
  const shown = mode === "code" ? held : null;
  return {
    parts,
    shown,
    step: mode === "code" ? "code" : state.kind,
    problem: create.error ?? remove.error,
  };
}

function CodeStep({
  link,
  crew,
  mobile,
  onClose,
  onNewCode,
}: {
  link: EmailLink;
  crew: string;
  mobile: boolean;
  onClose: () => void;
  onNewCode: (linkId: string) => void;
}) {
  const { parts, shown, step, problem } = useCodeStep(link, onClose, onNewCode);
  const buttons = shown && !mobile;
  return (
    <div data-email-step={step} className="flex flex-col">
      <Head
        mobile={mobile}
        eyebrow="Step 2 of 2"
        title={CODE_TITLE[mobile ? "phone" : "desktop"]}
        line={codeLine(parts.mode, link.address, mobile)}
      />
      <ErrorLine error={problem} mobile={mobile} />
      <CodePanel code={shown?.code ?? null} from={link.address} crew={crew} mobile={mobile}>
        {buttons ? <PanelButtons mailto={shown.sms_url} code={shown.code} crew={crew} /> : null}
      </CodePanel>
      {mobile ? (
        <PhoneActions {...parts} mailto={shown?.sms_url ?? null} />
      ) : (
        <DesktopActions {...parts} />
      )}
    </div>
  );
}

// ── 3. Linked ───────────────────────────────────────────────────────────

/** Crew as a contact (Paper 73): its mark, its name and the address. */
function CrewContact({ crew, className }: { crew: string; className?: string }) {
  return (
    <div
      className={cn("flex items-center gap-3 rounded-lg bg-background px-3.5 py-3.5", className)}
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-foreground font-display text-[22px] leading-5 text-background">
        C
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="text-body leading-5 font-semibold text-foreground">Crew</span>
        <span className="text-xs leading-4 wrap-anywhere text-muted-foreground">{crew}</span>
      </span>
    </div>
  );
}

function LinkedStep({
  link,
  crew,
  mobile,
  onClose,
}: {
  link: EmailLink;
  crew: string;
  mobile: boolean;
  onClose: () => void;
}) {
  const [copied, copy] = useCopy();
  const head = (
    <Head
      mobile={mobile}
      eyebrow="Linked"
      tone="moss"
      title="Your crew will answer it now"
      line={`${link.address} is linked. Email your crew from it anytime, or forward it mail.`}
    />
  );
  const save = (
    <div className="flex flex-1 flex-col gap-1.5">
      <span className="text-body leading-5.5 font-semibold text-foreground">Save it as Crew</span>
      <span className="text-sm leading-5.25 text-copy">
        Add your crew's address to your contacts, so it's one tap away and its replies never land in
        spam.
      </span>
    </div>
  );
  if (mobile) {
    return (
      <div data-email-step="linked" className="flex flex-col">
        {head}
        <div className="flex flex-col gap-3.5 px-5 pt-4.5">
          <CrewContact crew={crew} />
          {save}
        </div>
        <div className="flex flex-col gap-2.5 px-5 pt-4.5 pb-8.5">
          <Button size="lg" className={BIG} onClick={onClose}>
            Done
          </Button>
          <Button
            variant="outline"
            size="lg"
            className={cn(BIG, "bg-card")}
            onClick={() => copy(crew)}
          >
            {copied ? "Copied" : "Copy address"}
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div data-email-step="linked" className="flex flex-col">
      {head}
      <div className="flex items-center gap-6 px-7 py-6">
        <CrewContact crew={crew} className="w-64 shrink-0" />
        {save}
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-border px-7 py-4">
        <Button variant="outline" size="lg" className="bg-card" onClick={() => copy(crew)}>
          {copied ? "Copied" : "Copy address"}
        </Button>
        <Button size="lg" onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  );
}
